import { RGBColor, rgb } from 'd3-color';

// Conversion between the HSV color space and RGB is not exact.
// These algorithms approximate the colors between the two color
// spaces. However, the conversions are not always symmetric.
//
// For example, using https://www.rapidtables.com/convert/color/rgb-to-hsv.html
// RGB '#7B68EE' converts to H: 249 S: 56.3% V: 93.3%
// Then using https://www.rapidtables.com/convert/color/hsv-to-rgb.html
// H: 249 S: 56.3% V: 93.3% converts to '#7C68EE'
//
// There is no perceptual difference between the colors so these minor
// variations are acceptable.

/**
 * Gets the hue, saturation and value values for a rgb color string
 * @param rgbColor
 * @returns {h, s, v}
 */
export function getHSV(rgbColor: string) {
  return convertRGBtoHSV(rgb(rgbColor));
}

/**
 * Creates an RGBColor object matching the specified hue,
 * saturation and value.
 * @param h Number in range [0, 360)
 * @param s Number in range [0, 1]
 * @param v Number in range [0, 1]
 * @returns RGBColor object
 */
export function getRGB(h: number, s: number, v: number) {
  // Adapted from https://en.wikipedia.org/wiki/HSL_and_HSV
  const chroma = s * v;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - chroma;

  const [r1, g1, b1] = (() => {
    if (h < 60) {
      return [chroma, x, 0];
    } else if (h < 120) {
      return [x, chroma, 0];
    } else if (h < 180) {
      return [0, chroma, x];
    } else if (h < 240) {
      return [0, x, chroma];
    } else if (h < 300) {
      return [x, 0, chroma];
    } else {
      return [chroma, 0, x];
    }
  })();
  return rgb((r1 + m) * 255, (g1 + m) * 255, (b1 + m) * 255);
}

/**
 * Creates an RGB hex string matching the specified hue,
 * saturation and value.
 * @param h Number in range [0, 360)
 * @param s Number in range [0, 1]
 * @param v Number in range [0, 1]
 * @returns string rgb hex code
 */
export function hsvAsHex(h: number, s: number, v: number) {
  return getRGB(h, s, v).formatHex();
}

// Adapted from https://en.wikipedia.org/wiki/HSL_and_HSV
function convertRGBtoHSV(rgbColor: RGBColor) {
  // Convert r, g, b to percentages
  const r = rgbColor.r / 255;
  const g = rgbColor.g / 255;
  const b = rgbColor.b / 255;
  // Calculate the chroma
  const min = Math.min(r, g, b);
  const max = Math.max(r, g, b);
  const chroma = max - min;
  // Calculate the hue
  let h = NaN;
  if (chroma > 0) {
    if (r === max) {
      h = (g - b) / chroma + (g < b ? 6 : 0);
    } else if (g === max) {
      h = (b - r) / chroma + 2;
    } else {
      h = (r - g) / chroma + 4;
    }
    h *= 60;
  }
  // Calculate the saturation
  const s = max == 0 ? NaN : chroma / max;
  // Calculate the value
  const v = max;

  return { h, s, v };
}
